import { StopTimeType, StopType } from "types/MultiStop";

type ScheduleStopLike = Record<string, unknown> & {
  stop_type?: number;
  stop_key?: string;
};

type ScheduleLike = Record<string, unknown> & {
  stops?: ScheduleStopLike[];
};

function pickFirstDefined(...values: unknown[]): unknown {
  return values.find((value) => value !== undefined && value !== null);
}

function normalizeClassicTime(time: unknown): string | null {
  if (time === undefined || time === null || time === "") return null;
  const timeStr = String(time);
  const match = timeStr.match(/^(\d{2}:\d{2}:\d{2})/);
  return match ? match[1] : timeStr;
}

function mapCargoReady(value: unknown, isPickup: boolean): StopTimeType | undefined {
  if (value === undefined || value === null || value === "" || value === "unspecified") {
    return undefined;
  }

  const cargoReady = String(value).toLowerCase();

  if (isPickup) {
    if (cargoReady === "after" || cargoReady === "asap_pickup") return StopTimeType.AFTER;
    if (cargoReady === "between") return StopTimeType.BETWEEN;
    return undefined;
  }

  if (cargoReady === "before") return StopTimeType.BEFORE;
  if (cargoReady === "between") return StopTimeType.BETWEEN;
  if (cargoReady === "asap_delivery") return StopTimeType.ASAP_DELIVERY;
  return undefined;
}

function clearStopDatetimes(stop: ScheduleStopLike) {
  stop.stop_after_datetime = null;
  stop.stop_before_datetime = null;
}

function applyPickupPlanning(schedule: ScheduleLike, stop: ScheduleStopLike): ScheduleStopLike {
  const next: ScheduleStopLike = { ...stop };

  const afterDate = pickFirstDefined(schedule.pickup_ready_date, schedule.pickup_after_date);
  const rawAfterTime = pickFirstDefined(schedule.pickup_ready_time, schedule.pickup_after_time);
  const beforeDate = schedule.pickup_before_date;
  const rawBeforeTime = schedule.pickup_before_time;

  if (afterDate !== undefined) next.stop_after_date = afterDate;
  if (rawAfterTime !== undefined) next.stop_after_time = normalizeClassicTime(rawAfterTime);
  if (beforeDate !== undefined) next.stop_before_date = beforeDate;
  if (rawBeforeTime !== undefined) next.stop_before_time = normalizeClassicTime(rawBeforeTime);

  if (schedule.pickup_hours_from !== undefined) next.stop_hours_start = schedule.pickup_hours_from;
  if (schedule.pickup_hours_to !== undefined) next.stop_hours_end = schedule.pickup_hours_to;
  if (schedule.pickup_timezone !== undefined && schedule.pickup_timezone !== null && schedule.pickup_timezone !== "") {
    next.stop_timezone = schedule.pickup_timezone;
  }

  const stopTimeType = mapCargoReady(schedule.pickup_cargo_ready, true);
  if (stopTimeType !== undefined) {
    next.stop_time_type = stopTimeType;
  }

  // Modal "after" only edits pickup_ready_*; always mirror so stale pickup_before_*
  // from the duplicate payload cannot keep the old date on Confirm.
  if (next.stop_time_type === StopTimeType.AFTER) {
    next.stop_before_date = next.stop_after_date ?? null;
    next.stop_before_time = next.stop_after_time ?? null;
  }

  clearStopDatetimes(next);
  return next;
}

function applyDeliveryPlanning(schedule: ScheduleLike, stop: ScheduleStopLike): ScheduleStopLike {
  const next: ScheduleStopLike = { ...stop };

  const stopTimeType = mapCargoReady(schedule.delivery_cargo_ready, false);
  if (stopTimeType !== undefined) {
    next.stop_time_type = stopTimeType;
  }

  if (next.stop_time_type === StopTimeType.ASAP_DELIVERY) {
    next.stop_after_date = null;
    next.stop_after_time = null;
    next.stop_before_date = null;
    next.stop_before_time = null;
    next.stop_timezone = null;
    clearStopDatetimes(next);
    if (schedule.delivery_hours_from !== undefined) next.stop_hours_start = schedule.delivery_hours_from;
    if (schedule.delivery_hours_to !== undefined) next.stop_hours_end = schedule.delivery_hours_to;
    return next;
  }

  const afterDate = pickFirstDefined(schedule.stop1_after_date, schedule.stop1_date);
  const rawAfterTime = pickFirstDefined(schedule.stop1_after_time, schedule.stop1_time);
  const beforeDate = schedule.stop1_before_date;
  const rawBeforeTime = schedule.stop1_before_time;

  if (afterDate !== undefined) next.stop_after_date = afterDate;
  if (rawAfterTime !== undefined) next.stop_after_time = normalizeClassicTime(rawAfterTime);
  if (beforeDate !== undefined) next.stop_before_date = beforeDate;
  if (rawBeforeTime !== undefined) next.stop_before_time = normalizeClassicTime(rawBeforeTime);

  if (schedule.delivery_hours_from !== undefined) next.stop_hours_start = schedule.delivery_hours_from;
  if (schedule.delivery_hours_to !== undefined) next.stop_hours_end = schedule.delivery_hours_to;
  if (schedule.stop1_timezone !== undefined && schedule.stop1_timezone !== null && schedule.stop1_timezone !== "") {
    next.stop_timezone = schedule.stop1_timezone;
  }

  // Modal "before" only edits stop1_after_*; RFV2 Before UI reads stop_before_*.
  // Always mirror so stale stop1_before_* from hydrate/API cannot keep the old date.
  if (next.stop_time_type === StopTimeType.BEFORE) {
    next.stop_before_date = next.stop_after_date ?? null;
    next.stop_before_time = next.stop_after_time ?? null;
  }

  clearStopDatetimes(next);
  return next;
}

function findDeliveryStopIndex(stops: ScheduleStopLike[]): number {
  const dropoffIndex = stops.findIndex((stop) => stop.stop_type === StopType.DROPOFF);
  return dropoffIndex >= 0 ? dropoffIndex : 1;
}

/**
 * Load Template modal writes classic flat planning fields. When the duplicated
 * payload already has stops[], V1 multistop and RFV2 bind the UI to stops and
 * would ignore those flat overrides. Apply modal planning onto pickup + first
 * delivery stops before form reset/normalize.
 */
export function applyModalSchedulePlanningToStops(schedule: ScheduleLike | undefined): ScheduleLike | undefined {
  if (!schedule) return schedule;

  const { stops } = schedule;
  if (!Array.isArray(stops) || stops.length < 2) {
    return schedule;
  }

  const nextStops = stops.map((stop) => ({ ...stop }));
  nextStops[0] = applyPickupPlanning(schedule, nextStops[0]);

  const deliveryIndex = findDeliveryStopIndex(nextStops);
  if (deliveryIndex > 0 && deliveryIndex < nextStops.length) {
    nextStops[deliveryIndex] = applyDeliveryPlanning(schedule, nextStops[deliveryIndex]);
  }

  return {
    ...schedule,
    stops: nextStops,
  };
}
